import React, { useCallback, useEffect, useReducer, useState } from "react";
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControl, IconButton, InputAdornment, InputLabel, MenuItem, Paper,
  Select, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography, Avatar
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import Edit from "@mui/icons-material/Edit";
import Search from "@mui/icons-material/Search";
import ShareIcon from "@mui/icons-material/Share";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadDoneIcon from "@mui/icons-material/DownloadDone";
import ImageIcon from "@mui/icons-material/Image";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import TableRowSkeleton from "../../components/TableRowSkeleton";

const MainPaper = styled(Paper)(({ theme }) => ({
  flex: 1, padding: theme.spacing(2), margin: theme.spacing(1),
  overflowY: "scroll", ...theme.scrollbarStyles,
  borderRadius: theme.shape.borderRadius, boxShadow: theme.shadows[1],
}));

// ── Product Modal ──────────────────────────────────────────────────────────────
const ProductModal = ({ open, onClose, product }) => {
  const theme = useTheme();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [trackingPixelId, setTrackingPixelId] = useState("");
  const [pixels, setPixels] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/tracking/pixels").then(r => setPixels(r.data || [])).catch(() => setPixels([]));
    if (product) {
      setName(product.name || "");
      setDescription(product.description || "");
      setTrackingPixelId(product.trackingPixelId ? String(product.trackingPixelId) : "");
      setPreviewUrl(product.image ? `${process.env.REACT_APP_BACKEND_URL}/public/${product.image}` : null);
    } else {
      setName(""); setDescription(""); setTrackingPixelId(""); setPreviewUrl(null);
    }
    setImageFile(null);
  }, [open, product]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Nome é obrigatório"); return; }
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("name", name);
      if (description) formData.append("description", description);
      if (trackingPixelId) formData.append("trackingPixelId", trackingPixelId);
      if (imageFile) formData.append("image", imageFile);
      if (product) {
        await api.put(`/products/${product.id}`, formData);
        toast.success("Produto atualizado!");
      } else {
        await api.post("/products", formData);
        toast.success("Produto criado!");
      }
      onClose(true);
    } catch (err) { toastError(err); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ backgroundColor: theme.palette.primary.main, color: "#fff", py: 1.5 }}>
        <Typography fontWeight={700}>{product ? "Editar Produto" : "Novo Produto"}</Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 0.5 }}>
          {/* Image upload */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <label htmlFor="product-image-upload" style={{ cursor: "pointer" }}>
              <Avatar
                src={previewUrl}
                variant="rounded"
                sx={{ width: 72, height: 72, backgroundColor: theme.palette.grey[200], border: `2px dashed ${theme.palette.divider}` }}
              >
                <ImageIcon sx={{ color: theme.palette.text.disabled }} />
              </Avatar>
            </label>
            <input id="product-image-upload" type="file" accept="image/*" style={{ display: "none" }} onChange={handleImageChange} />
            <Box>
              <Typography variant="body2" fontWeight={600}>Imagem do produto</Typography>
              <Typography variant="caption" color="textSecondary">Clique na imagem para alterar</Typography>
            </Box>
          </Box>
          <TextField label="Nome *" value={name} onChange={e => setName(e.target.value)} fullWidth size="small" />
          <TextField label="Descrição (opcional)" value={description} onChange={e => setDescription(e.target.value)} fullWidth size="small" multiline rows={2} />
          {pixels.length > 0 && (
            <Box sx={{ mt: 0.5 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Pixel Facebook (opcional)</InputLabel>
                <Select value={trackingPixelId} label="Pixel Facebook (opcional)" onChange={e => setTrackingPixelId(e.target.value)}>
                  <MenuItem value=""><em>Nenhum</em></MenuItem>
                  {pixels.map(px => (
                    <MenuItem key={px.id} value={String(px.id)}>
                      {px.name || px.pixelId}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ backgroundColor: "#f5f5f5" }}>
        <Button onClick={() => onClose(false)} size="small">Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving} size="small">
          {saving ? "Salvando..." : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Share Code Dialog ──────────────────────────────────────────────────────────
const ShareCodeDialog = ({ open, onClose, product }) => {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open || !product) return;
    if (product.shareCode) { setCode(product.shareCode); return; }
    setLoading(true);
    api.post(`/products/${product.id}/share-code`)
      .then(({ data }) => setCode(data.shareCode))
      .catch(err => toastError(err))
      .finally(() => setLoading(false));
  }, [open, product]);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Compartilhar Produto</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Compartilhe este código para que outra conta importe este produto com todas as mensagens e Speed Modes vinculados.
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TextField
            fullWidth size="small" value={loading ? "Gerando..." : code}
            InputProps={{ readOnly: true }}
            sx={{ "& input": { fontFamily: "monospace", fontWeight: 700, letterSpacing: 2, fontSize: "1.1rem" } }}
          />
          <Tooltip title={copied ? "Copiado!" : "Copiar"}>
            <IconButton onClick={handleCopy} color={copied ? "success" : "default"}>
              {copied ? <DownloadDoneIcon /> : <ContentCopyIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} size="small">Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Import by Code Dialog ──────────────────────────────────────────────────────
const ImportCodeDialog = ({ open, onClose }) => {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const handleImport = async () => {
    if (!code.trim()) return;
    setLoading(true);
    try {
      await api.post("/products/import-by-code", { code: code.trim() });
      toast.success("Produto importado com sucesso!");
      onClose(true);
    } catch (err) { toastError(err); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="xs" fullWidth>
      <DialogTitle>Importar Produto por Código</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Cole o código de compartilhamento para importar um produto com todas as mensagens e Speed Modes vinculados.
        </Typography>
        <TextField
          autoFocus fullWidth size="small" label="Código de compartilhamento"
          value={code} onChange={e => setCode(e.target.value.toUpperCase())}
          inputProps={{ style: { fontFamily: "monospace", letterSpacing: 2 } }}
          onKeyPress={e => { if (e.key === "Enter") handleImport(); }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)} size="small">Cancelar</Button>
        <Button variant="contained" onClick={handleImport} disabled={loading || !code.trim()} size="small">
          {loading ? "Importando..." : "Importar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Main Products Page ─────────────────────────────────────────────────────────
const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [deletingProduct, setDeletingProduct] = useState(null);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/products");
      setProducts(data || []);
    } catch (err) { toastError(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  const handleModalClose = (reload) => {
    setModalOpen(false);
    setSelectedProduct(null);
    if (reload) loadProducts();
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/products/${deletingProduct.id}`);
      toast.success("Produto excluído");
      setProducts(prev => prev.filter(p => p.id !== deletingProduct.id));
    } catch (err) { toastError(err); }
    setDeletingProduct(null);
    setConfirmOpen(false);
  };

  const filtered = products.filter(p =>
    !search || p.name?.toLowerCase().includes(search.toLowerCase()) || p.description?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <MainContainer>
      <ConfirmationModal
        title="Excluir produto"
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={handleDelete}
      >
        Tem certeza? Isso não remove as mensagens e Speed Modes vinculados.
      </ConfirmationModal>

      <ProductModal
        open={modalOpen}
        onClose={handleModalClose}
        product={selectedProduct}
      />

      <ShareCodeDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        product={selectedProduct}
      />

      <ImportCodeDialog
        open={importOpen}
        onClose={(reload) => { setImportOpen(false); if (reload) loadProducts(); }}
      />

      <MainHeader>
        <Title>Produtos {products.length > 0 ? `(${products.length})` : ""}</Title>
        <MainHeaderButtonsWrapper>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <TextField
              placeholder="Buscar produtos..."
              type="search" size="small" value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search color="secondary" /></InputAdornment> }}
              sx={{ flex: 1, minWidth: 200, "& .MuiOutlinedInput-root": { height: 40 } }}
            />
            <Tooltip title="Importar por código">
              <Button variant="outlined" color="primary" onClick={() => setImportOpen(true)} sx={{ height: 40, textTransform: "none" }}>
                Importar
              </Button>
            </Tooltip>
            <Tooltip title="Novo produto">
              <Button variant="contained" color="primary" onClick={() => { setSelectedProduct(null); setModalOpen(true); }} sx={{ height: 40 }}>
                <AddCircleOutline />
              </Button>
            </Tooltip>
          </Box>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <MainPaper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">Imagem</TableCell>
              <TableCell align="center">Nome</TableCell>
              <TableCell align="center">Mensagens</TableCell>
              <TableCell align="center">Speed Modes</TableCell>
              <TableCell align="center">Código</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
                    Nenhum produto cadastrado
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filtered.map(p => (
                <TableRow key={p.id} hover>
                  <TableCell align="center">
                    <Avatar
                      src={p.image ? `${process.env.REACT_APP_BACKEND_URL}/public/${p.image}` : undefined}
                      variant="rounded"
                      sx={{ width: 36, height: 36, margin: "0 auto", backgroundColor: "grey.200" }}
                    >
                      <ImageIcon fontSize="small" />
                    </Avatar>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight={600}>{p.name}</Typography>
                    {p.description && <Typography variant="caption" color="textSecondary">{p.description}</Typography>}
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={p.quickAnswers?.length || 0} size="small" color="primary" variant="outlined" />
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={p.scripts?.length || 0} size="small" color="secondary" variant="outlined" />
                  </TableCell>
                  <TableCell align="center">
                    {p.shareCode ? (
                      <Typography variant="caption" sx={{ fontFamily: "monospace", fontWeight: 700, letterSpacing: 1 }}>
                        {p.shareCode}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="textDisabled">—</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Compartilhar">
                      <IconButton size="small" onClick={() => { setSelectedProduct(p); setShareOpen(true); }}>
                        <ShareIcon color="primary" />
                      </IconButton>
                    </Tooltip>
                    <IconButton size="small" onClick={() => { setSelectedProduct(p); setModalOpen(true); }}>
                      <Edit color="info" />
                    </IconButton>
                    <IconButton size="small" onClick={() => { setDeletingProduct(p); setConfirmOpen(true); }}>
                      <DeleteOutline color="error" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
            {loading && <TableRowSkeleton columns={6} />}
          </TableBody>
        </Table>
      </MainPaper>
    </MainContainer>
  );
};

export default Products;
